/*
 * <read-more-text> clamps its text to a number of lines and reveals the rest on click.
 *
 * The clamp is applied here rather than in the markup so that, without JavaScript, the
 * full text renders and no non-working button is shown. The button stays hidden when the
 * text is short enough to fit, so short descriptions never get a pointless "Read more".
 */
if (!customElements.get('read-more-text')) {
  customElements.define(
    'read-more-text',
    class ReadMoreText extends HTMLElement {
      connectedCallback() {
        this.textEl = this.querySelector('[data-description-text]');
        this.button = this.querySelector('[data-description-toggle]');
        if (!this.textEl || !this.button) return;

        this.moreLabel = this.querySelector('[data-more-label]');
        this.lessLabel = this.querySelector('[data-less-label]');

        this.onToggle = this.toggle.bind(this);
        this.button.addEventListener('click', this.onToggle);

        // Clamp first, then measure against the clamped height.
        this.setAttribute('data-clamped', 'true');
        this.evaluate();

        // Web fonts and lazy images change the line count after first paint.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.evaluate()).catch(() => {});
        }

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.evaluate());
          this.resizeObserver.observe(this);
        }
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.button && this.onToggle) this.button.removeEventListener('click', this.onToggle);
      }

      get expanded() {
        return this.getAttribute('data-expanded') === 'true';
      }

      // Show the button only when the text actually overflows the clamp.
      evaluate() {
        if (this.expanded) return;
        // Nothing to measure while hidden, e.g. inside a collapsed <details>.
        if (!this.textEl.clientHeight) return;
        this.button.hidden = this.textEl.scrollHeight <= this.textEl.clientHeight + 1;
      }

      toggle() {
        const next = !this.expanded;
        this.setAttribute('data-expanded', next ? 'true' : 'false');
        this.button.setAttribute('aria-expanded', next ? 'true' : 'false');
        if (this.moreLabel) this.moreLabel.hidden = next;
        if (this.lessLabel) this.lessLabel.hidden = !next;
        if (!next) this.keepInView();
      }

      // The sticky header overlaps the top of the page, so leave room for it.
      get headerOffset() {
        const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
        const value = parseFloat(raw);
        return Number.isFinite(value) ? value : 0;
      }

      /*
       * Collapsing removes a lot of height from above the fold. Without this the scroll
       * position is unchanged, so the customer is left looking at whatever has moved up
       * into view, usually the reviews, rather than the row they just closed.
       * Only scrolls when the row has actually been pushed off the top of the screen.
       */
      keepInView() {
        const anchor = this.closest('.product__accordion') || this;
        const top = anchor.getBoundingClientRect().top;
        const offset = this.headerOffset + 16;
        if (top >= offset) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({
          top: Math.max(0, top + window.scrollY - offset),
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }
    }
  );
}
